/**
 * Authentication Utilities
 * 
 * This module provides enterprise-standard authentication utilities including
 * token management, authentication state checking, and secure storage operations.
 * 
 * Security Best Practices:
 * - Uses localStorage for token storage (can be upgraded to httpOnly cookies for production)
 * - Validates token format before use
 * - Provides clear error handling
 * - Follows consistent naming conventions
 */

// Constants
const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
const TOKEN_EXPIRY_KEY = 'token_expiry';

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated, false otherwise
 */
export const isAuthenticated = () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    
    // Check if token exists
    if (!token) {
      return false;
    }
    
    // Check if token has expired (if expiry is set)
    if (expiry) {
      const expiryDate = new Date(expiry);
      const now = new Date();
      if (now > expiryDate) {
        // Token has expired, clear it
        clearAuth();
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking authentication status:', error);
    return false;
  }
};

/**
 * Get the authentication token
 * @returns {string|null} The authentication token or null if not authenticated
 */
export const getAuthToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

/**
 * Set the authentication token
 * @param {string} token - The authentication token
 * @param {number} expiresIn - Optional expiry time in seconds (default: 24 hours)
 */
export const setAuthToken = (token, expiresIn = 86400) => {
  try {
    // Validate token format
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token format');
    }
    
    // Store token
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    
    // Set expiry time
    const expiryDate = new Date();
    expiryDate.setSeconds(expiryDate.getSeconds() + expiresIn);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryDate.toISOString());
    
    return true;
  } catch (error) {
    console.error('Error setting auth token:', error);
    return false;
  }
};

/**
 * Get the authenticated user data
 * @returns {object|null} User data object or null if not authenticated
 */
export const getAuthUser = () => {
  try {
    const userJson = localStorage.getItem(AUTH_USER_KEY);
    if (!userJson) {
      return null;
    }
    
    return JSON.parse(userJson);
  } catch (error) {
    console.error('Error getting auth user:', error);
    return null;
  }
};

/**
 * Set the authenticated user data
 * @param {object} user - User data object
 * @returns {boolean} True if successful, false otherwise
 */
export const setAuthUser = (user) => {
  try {
    // Validate user data
    if (!user || typeof user !== 'object') {
      throw new Error('Invalid user data');
    }
    
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    return true;
  } catch (error) {
    console.error('Error setting auth user:', error);
    return false;
  }
};

/**
 * Clear authentication data (logout)
 * @returns {boolean} True if successful, false otherwise
 */
export const clearAuth = () => {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing auth data:', error);
    return false;
  }
};

/**
 * Login function - stores authentication data
 * @param {string} token - Authentication token
 * @param {object} user - User data
 * @param {number} expiresIn - Optional expiry time in seconds
 * @returns {boolean} True if successful, false otherwise
 */
export const login = (token, user, expiresIn) => {
  try {
    const tokenSet = setAuthToken(token, expiresIn);
    const userSet = setAuthUser(user);
    
    return tokenSet && userSet;
  } catch (error) {
    console.error('Error during login:', error);
    return false;
  }
};

/**
 * Logout function - clears authentication data
 * @returns {boolean} True if successful, false otherwise
 */
export const logout = () => {
  return clearAuth();
};

/**
 * Get authorization header for API requests
 * @returns {object} Authorization header object
 */
export const getAuthHeader = () => {
  const token = getAuthToken();
  if (!token) {
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Validate token format (basic validation)
 * @param {string} token - Token to validate
 * @returns {boolean} True if token format is valid, false otherwise
 */
export const validateTokenFormat = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }
  
  // Basic JWT validation (can be enhanced based on your token format)
  const parts = token.split('.');
  return parts.length === 3; // JWT has 3 parts
};
